import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import axios from 'axios';
import { confirmAction } from './useConfirm';
import { useToast } from './useToast';
import {
    RTB_CAMPAIGN_INDEX_PATH,
    RTB_CAMPAIGN_NAME_CHARSET_MESSAGE,
    RTB_CAMPAIGN_NAME_PATTERN,
    RTB_CAMPAIGN_SAVE_ENDPOINT,
    RTB_CAMPAIGN_TYPE,
    RTB_DISPLAY_STATUS,
    RTB_MATERIAL_FIELDS,
    RTB_STATUS_HINT,
} from '../constants/rtbCampaigns';
import { buildMissingTargetsTooltip, isCreativeReadyForImpressions as resolveCreativeReadiness } from '../utils/creativeStatus';
import { periodLengthDays } from '../utils/datePeriods';
import { extractFieldErrors, extractErrorMessage, notifyHttpError } from '../utils/httpErrors';
import { exportCampaignSettings, importCampaignSettings } from '../utils/rtbCampaignSettingsFile';
import { formatUncoveredScreensNotice, uncoveredScreensBySize } from '../utils/rtbCreativeCoverage';
import { getRtbDateString } from '../utils/timezones';

const emptyForm = () => ({
    name: '',
    start_date: '',
    end_date: '',
    budget_total_limit: 0,
    impr_total_limit: 0,
    budget_daily_limit: null,
    impr_daily_limit: null,
    status: RTB_DISPLAY_STATUS.PAUSED,
    system_status: RTB_DISPLAY_STATUS.PENDING,
    advertiser_id: null,
    brand_id: null,
    screen_package_id: null,
    poi_layer_id: null,
});

const dateInputValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    return getRtbDateString(new Date(value));
};

const sortedIds = (ids) => [...ids].map(Number).filter(id => !Number.isNaN(id)).sort((a, b) => a - b);
const sortedGids = (gids) => [...gids].map(String).filter(Boolean).sort();

/**
 * Общее состояние формы RTB-кампании (ручной и пакетной): гидратация, валидация, сохранение,
 * аудиторные данные, готовность креативов, предупреждение о модерации.
 */
export const useRtbCampaignForm = (props, { selectedFrameGids, frames, extraPayload, extraValidate, onHydrated, onSettingsApplied } = {}) => {
    const toast = useToast();
    const type = computed(() => props.type || RTB_CAMPAIGN_TYPE.MANUAL);
    const isEditMode = computed(() => Boolean(props.campaign?.id));
    const canWrite = computed(() => props.userData?.can_write !== false);
    const isReadOnly = computed(() => isEditMode.value && !canWrite.value);
    const today = computed(() => getRtbDateString());
    const isAdmin = computed(() => Boolean(props.userData?.is_admin));
    const isAgency = computed(() => Boolean(props.userData?.is_agency));

    const form = reactive(emptyForm());
    const errors = ref({});
    const generalError = ref('');
    const saving = ref(false);
    const savingAsDraft = ref(false);
    const exportingSettings = ref(false);
    const importingSettings = ref(false);
    const isCurrentlyDraft = ref(false);
    const selectedCreativeIds = ref([]);
    const campaignSchedule = ref([]);
    const audienceEnabled = ref(false);
    const audienceLocked = ref(false);
    const audiencePreview = ref(null);
    const creativeReadinessById = ref({});
    const distributeEvenly = ref(false);
    const settingsFileInput = ref(null);
    const dirty = ref(false);
    let audiencePreviewTimer = null;
    let readinessRefreshTimer = null;
    let initialSnapshot = '';
    let skipDirty = true;

    const pageTitle = computed(() => {
        const titles = {
            [RTB_CAMPAIGN_TYPE.PACKAGE]: ['Просмотр кампании по пакету', 'Редактировать кампанию по пакету', 'Создать кампанию по пакету'],
            [RTB_CAMPAIGN_TYPE.POI]: ['Просмотр POI-кампании', 'Редактировать POI-кампанию', 'Создать POI-кампанию'],
        };
        const [view, edit, create] = titles[type.value] || ['Просмотр кампании RTB', 'Редактировать кампанию RTB', 'Создать кампанию RTB'];
        if (isReadOnly.value) return view;
        return isEditMode.value ? edit : create;
    });

    const cancelUrl = computed(() => RTB_CAMPAIGN_INDEX_PATH[type.value]);
    const isStartDateLocked = computed(() => Boolean(isEditMode.value && props.campaign?.start_date_locked));
    const endDateMin = computed(() => (isEditMode.value && props.campaign?.end_date_min) || form.start_date || today.value);

    const filteredBrands = computed(() => {
        if (!form.advertiser_id) return [];
        return props.advertisers.find(advertiser => advertiser.id === form.advertiser_id)?.brands || [];
    });

    /** Агентство и клиент: пустое поле заполняется, если вариант единственный. Админ выбирает сам. */
    const applySoleIdentity = () => {
        if (isAdmin.value) return;

        const advertisers = props.advertisers || [];
        if (!form.advertiser_id && advertisers.length === 1) {
            form.advertiser_id = advertisers[0].id;
        }

        const brands = filteredBrands.value;
        if (form.advertiser_id && !form.brand_id && brands.length === 1) {
            form.brand_id = brands[0].id;
        }
    };

    const allCreatives = computed(() => {
        const byId = new Map();
        for (const creative of props.availableCreatives || []) {
            if (creative?.id == null) continue;
            byId.set(Number(creative.id), creative);
        }
        for (const creative of props.campaignCreatives || []) {
            if (creative?.id == null) continue;
            const id = Number(creative.id);
            byId.set(id, { ...(byId.get(id) || {}), ...creative });
        }
        return [...byId.values()].sort((a, b) => Number(b.id) - Number(a.id));
    });

    const campaignDays = computed(() => periodLengthDays(form.start_date, form.end_date) || 0);

    const hasAtLeastOneLimit = computed(() => (
        Number(form.budget_total_limit) > 0
        || Number(form.impr_total_limit) > 0
        || Number(form.budget_daily_limit) > 0
        || Number(form.impr_daily_limit) > 0
    ));

    const resolveCreative = (id) => {
        const n = Number(id);
        return allCreatives.value.find(creative => Number(creative.id) === n) || null;
    };

    const selectedCreativeObjects = computed(() => selectedCreativeIds.value.map(resolveCreative).filter(Boolean));

    const catalogFrames = () => (frames ? frames.value : props.frames) || [];

    const uncoveredScreenGroups = computed(() => {
        if (type.value !== RTB_CAMPAIGN_TYPE.MANUAL && type.value !== RTB_CAMPAIGN_TYPE.POI) return [];
        return uncoveredScreensBySize(catalogFrames(), selectedFrameGids?.value || [], selectedCreativeObjects.value);
    });
    const uncoveredScreensNotice = computed(() => formatUncoveredScreensNotice(uncoveredScreenGroups.value));

    const showCreativeReadinessHint = computed(() => !isEditMode.value && (selectedFrameGids?.value?.length || 0) === 0);

    const hasBeelineSelected = computed(() => (selectedFrameGids?.value || []).some((gid) => {
        const frame = catalogFrames().find(item => String(item.gid) === String(gid));
        return Boolean(frame?.has_beeline);
    }));

    const audienceMarkupPercent = computed(() => Number(props.audienceConfig?.markup_percent ?? 10));
    const audienceOfferUrl = computed(() => props.audienceConfig?.offer_url || '#');
    const audiencePayableHint = computed(() => {
        const media = Number(form.budget_total_limit) || 0;
        if (media <= 0 || !audienceEnabled.value) return '';
        const payable = Math.round(media * (1 + audienceMarkupPercent.value / 100) * 100) / 100;
        return `К оплате с аудиторными данными: ${new Intl.NumberFormat('ru-RU').format(payable)} ₸ (лимит медиа без изменения ставки).`;
    });

    const displayStatus = computed(() => props.campaign?.display_status || '');
    const displayStatusLabel = computed(() => props.campaign?.display_status_label || '');
    const statusHint = computed(() => RTB_STATUS_HINT[displayStatus.value] || '');

    const materialSnapshot = () => JSON.stringify({
        start_date: form.start_date || '',
        end_date: form.end_date || '',
        budget_total_limit: Number(form.budget_total_limit) || 0,
        budget_daily_limit: Number(form.budget_daily_limit) || 0,
        impr_total_limit: Number(form.impr_total_limit) || 0,
        impr_daily_limit: Number(form.impr_daily_limit) || 0,
        creative_ids: sortedIds(selectedCreativeIds.value),
        frame_gids: sortedGids(selectedFrameGids?.value || []),
        screen_package_id: form.screen_package_id || null,
    });

    const willRemoderate = computed(() => {
        if (!isEditMode.value || isCurrentlyDraft.value || isAdmin.value || isReadOnly.value) return false;
        if (props.campaign?.system_status === RTB_DISPLAY_STATUS.REJECTED) return true;
        if (!['active', 'exhausted', 'completed'].includes(props.campaign?.system_status)) return false;
        return materialSnapshot() !== initialSnapshot;
    });

    const canSaveDraft = computed(() => form.name.trim() !== '');

    const clientErrors = (asDraft, { extra = true } = {}) => {
        const result = {};
        if (!form.name.trim()) result.name = 'Укажите название кампании';
        else if (!RTB_CAMPAIGN_NAME_PATTERN.test(form.name)) result.name = RTB_CAMPAIGN_NAME_CHARSET_MESSAGE;
        if (asDraft) return result;
        if (!form.advertiser_id) result.advertiser_id = 'Выберите рекламодателя';
        if (!form.brand_id) result.brand_id = 'Выберите бренд';
        if (!form.start_date) result.start_date = 'Укажите дату начала';
        if (!form.end_date) result.end_date = 'Укажите дату окончания';
        if (form.start_date && form.end_date && form.end_date < form.start_date) {
            result.end_date = 'Дата окончания не может быть раньше даты начала';
        }
        if (selectedCreativeIds.value.length === 0) result.creative_ids = 'Выберите хотя бы один креатив';
        if (type.value === RTB_CAMPAIGN_TYPE.MANUAL || type.value === RTB_CAMPAIGN_TYPE.POI) {
            if (!hasAtLeastOneLimit.value) result.budget_total_limit = 'Укажите хотя бы один лимит: бюджет или показы, общий или дневной';
            if ((selectedFrameGids?.value || []).length === 0) {
                result.frame_gids = type.value === RTB_CAMPAIGN_TYPE.POI
                    ? 'В слое нет экранов для запуска'
                    : 'Выберите хотя бы один экран';
            }
        }
        if (type.value === RTB_CAMPAIGN_TYPE.POI && !form.poi_layer_id) {
            result.poi_layer_id = 'Выберите POI-слой';
        }
        if (type.value === RTB_CAMPAIGN_TYPE.PACKAGE && !form.screen_package_id) {
            result.screen_package_id = 'Выберите пакет экранов';
        }
        if (extra) Object.assign(result, extraValidate?.(asDraft) || {});
        return result;
    };

    const isValid = computed(() => Object.keys(clientErrors(false, { extra: false })).length === 0);

    const hydrate = () => {
        skipDirty = true;
        Object.assign(form, emptyForm());
        if (!isEditMode.value) {
            isCurrentlyDraft.value = false;
            onHydrated?.();
            applySoleIdentity();
            skipDirty = false;
            initialSnapshot = materialSnapshot();
            return;
        }

        const campaign = props.campaign;
        Object.assign(form, {
            name: campaign.name || '',
            start_date: dateInputValue(campaign.start_date),
            end_date: dateInputValue(campaign.end_date),
            budget_total_limit: Number(campaign.budget_total_limit) || 0,
            impr_total_limit: Number(campaign.impr_total_limit) || 0,
            budget_daily_limit: campaign.budget_daily_limit != null ? Number(campaign.budget_daily_limit) : null,
            impr_daily_limit: campaign.impr_daily_limit != null ? Number(campaign.impr_daily_limit) : null,
            status: campaign.status === RTB_DISPLAY_STATUS.DRAFT ? RTB_DISPLAY_STATUS.PAUSED : (campaign.status || RTB_DISPLAY_STATUS.PAUSED),
            system_status: campaign.system_status || RTB_DISPLAY_STATUS.PENDING,
            advertiser_id: campaign.advertiser_id || null,
            brand_id: campaign.brand_id || null,
            screen_package_id: campaign.screen_package_id || null,
            poi_layer_id: campaign.poi_layer_id || null,
        });
        isCurrentlyDraft.value = campaign.status === RTB_DISPLAY_STATUS.DRAFT;
        distributeEvenly.value = Boolean(campaign.distribute_evenly);
        campaignSchedule.value = campaign.schedule || [];
        audienceEnabled.value = Boolean(campaign.audience_data_enabled);
        audienceLocked.value = Boolean(campaign.audience_data_enabled);
        selectedCreativeIds.value = (props.campaignCreatives || [])
            .filter(creative => creative.pivot_enabled !== false && creative.pivot_enabled !== 0)
            .map(creative => Number(creative.id))
            .filter(id => !Number.isNaN(id));
        onHydrated?.();
        applySoleIdentity();
        initialSnapshot = materialSnapshot();
        skipDirty = false;
    };

    const markDirty = () => {
        if (!skipDirty) dirty.value = true;
    };

    const buildBasePayload = ({ asDraft = false } = {}) => ({
        name: form.name.trim(),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        budget_total_limit: form.budget_total_limit || 0,
        impr_total_limit: form.impr_total_limit || 0,
        budget_daily_limit: form.budget_daily_limit || null,
        impr_daily_limit: form.impr_daily_limit || null,
        distribute_evenly: distributeEvenly.value,
        schedule: campaignSchedule.value,
        status: asDraft ? RTB_DISPLAY_STATUS.DRAFT : (form.status === RTB_DISPLAY_STATUS.DRAFT ? RTB_DISPLAY_STATUS.PAUSED : (form.status || RTB_DISPLAY_STATUS.PAUSED)),
        system_status: form.system_status,
        advertiser_id: form.advertiser_id || null,
        brand_id: form.brand_id || null,
        poi_layer_id: form.poi_layer_id || null,
        creative_ids: selectedCreativeIds.value,
        audience_data_enabled: audienceEnabled.value,
        audience_offer_accepted: audienceEnabled.value,
        ...(extraPayload?.({ asDraft }) || {}),
    });

    const scrollToField = (field) => {
        const el = document.querySelector(`[data-field="${field}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const persistCampaign = async ({ asDraft = false } = {}) => {
        if (!canWrite.value || saving.value) return;
        generalError.value = '';
        errors.value = clientErrors(asDraft);
        if (Object.keys(errors.value).length) {
            scrollToField(Object.keys(errors.value)[0]);
            return;
        }

        if (!asDraft && uncoveredScreenGroups.value.length > 0) {
            const confirmed = await confirmAction({
                title: 'Экраны без креатива своего размера',
                message: `${uncoveredScreensNotice.value} Сохранить кампанию?`,
                confirmLabel: 'Сохранить',
            });
            if (!confirmed) return;
        }

        saving.value = true;
        savingAsDraft.value = asDraft;
        try {
            const payload = buildBasePayload({ asDraft });
            const url = isEditMode.value
                ? `${RTB_CAMPAIGN_SAVE_ENDPOINT[type.value]}/${props.campaign.id}`
                : RTB_CAMPAIGN_SAVE_ENDPOINT[type.value];
            const { data } = await axios[isEditMode.value ? 'put' : 'post'](url, payload);
            toast.success(data.message || (asDraft ? 'Черновик сохранён' : 'Кампания сохранена'));
            dirty.value = false;
            window.location.assign(data.redirect || RTB_CAMPAIGN_INDEX_PATH[type.value]);
        } catch (error) {
            errors.value = extractFieldErrors(error);
            if (Object.keys(errors.value).length) scrollToField(Object.keys(errors.value)[0]);
            else generalError.value = extractErrorMessage(error, 'Не удалось сохранить кампанию');
        } finally {
            saving.value = false;
            savingAsDraft.value = false;
        }
    };

    const submit = () => persistCampaign({ asDraft: false });
    const submitDraft = () => persistCampaign({ asDraft: true });

    const exportSettings = async () => {
        if (exportingSettings.value) return;
        exportingSettings.value = true;
        try {
            await exportCampaignSettings(type.value, buildBasePayload({ asDraft: isCurrentlyDraft.value }));
        } catch (error) {
            notifyHttpError(error, 'Не удалось скачать настройки');
        } finally {
            exportingSettings.value = false;
        }
    };

    const triggerSettingsImport = () => settingsFileInput.value?.click();

    const finiteOr = (value, fallback) => {
        if (value == null || value === '') return fallback;
        const number = Number(value);
        return Number.isFinite(number) ? number : fallback;
    };

    const applySettingsPayload = (payload) => {
        if (!payload || typeof payload !== 'object') return [];
        const warnings = [];
        skipDirty = true;
        form.name = payload.name == null ? '' : String(payload.name);
        if (isStartDateLocked.value) {
            const incomingStart = dateInputValue(payload.start_date);
            if (incomingStart && incomingStart !== form.start_date) {
                warnings.push('Дата старта уже зафиксирована и не изменена');
            }
        } else {
            form.start_date = dateInputValue(payload.start_date);
        }
        form.end_date = dateInputValue(payload.end_date);
        form.budget_total_limit = finiteOr(payload.budget_total_limit, 0);
        form.impr_total_limit = finiteOr(payload.impr_total_limit, 0);
        form.budget_daily_limit = finiteOr(payload.budget_daily_limit, null);
        form.impr_daily_limit = finiteOr(payload.impr_daily_limit, null);
        distributeEvenly.value = Boolean(payload.distribute_evenly);
        campaignSchedule.value = Array.isArray(payload.schedule) ? payload.schedule : [];
        if (payload.status === RTB_DISPLAY_STATUS.DRAFT) {
            isCurrentlyDraft.value = true;
            form.status = RTB_DISPLAY_STATUS.PAUSED;
        } else if (payload.status === RTB_DISPLAY_STATUS.ACTIVE || payload.status === RTB_DISPLAY_STATUS.PAUSED) {
            isCurrentlyDraft.value = false;
            form.status = payload.status;
        } else {
            isCurrentlyDraft.value = false;
            form.status = RTB_DISPLAY_STATUS.PAUSED;
        }
        if (isAdmin.value) form.system_status = payload.system_status || RTB_DISPLAY_STATUS.PENDING;
        if (payload.poi_layer_id) form.poi_layer_id = Number(payload.poi_layer_id);
        if (audienceLocked.value && payload.audience_data_enabled === false) {
            warnings.push('Аудиторные данные уже включены и не отключаются');
        } else if (!audienceLocked.value) {
            audienceEnabled.value = Boolean(payload.audience_data_enabled);
        }

        const advertiserIds = new Set((props.advertisers || []).map(advertiser => Number(advertiser.id)));
        if (payload.advertiser_id == null || payload.advertiser_id === '') {
            form.advertiser_id = null;
        } else if (advertiserIds.has(Number(payload.advertiser_id))) {
            form.advertiser_id = Number(payload.advertiser_id);
        } else {
            form.advertiser_id = null;
            warnings.push(`Рекламодатель ${payload.advertiser_id} недоступен`);
        }
        const brandOk = filteredBrands.value.some(brand => Number(brand.id) === Number(payload.brand_id));
        if (payload.brand_id == null || payload.brand_id === '') {
            form.brand_id = null;
        } else if (brandOk) {
            form.brand_id = Number(payload.brand_id);
        } else {
            form.brand_id = null;
            warnings.push(`Бренд ${payload.brand_id} недоступен`);
        }

        const availableCreativeIds = new Set(allCreatives.value.map(creative => Number(creative.id)));
        const requestedCreatives = Array.isArray(payload.creative_ids) ? payload.creative_ids.map(Number) : [];
        const missingCreatives = requestedCreatives.filter(id => Number.isFinite(id) && !availableCreativeIds.has(id));
        selectedCreativeIds.value = requestedCreatives.filter(id => availableCreativeIds.has(id));
        if (missingCreatives.length) warnings.push(`Креативы не найдены: ${missingCreatives.join(', ')}`);

        const applied = onSettingsApplied?.(payload);
        if (Array.isArray(applied)) warnings.push(...applied);
        skipDirty = false;
        markDirty();
        return warnings;
    };

    const onSettingsFileSelected = async (event) => {
        const file = event.target?.files?.[0];
        event.target.value = '';
        if (!file || importingSettings.value) return;
        importingSettings.value = true;
        try {
            const { payload, warnings } = await importCampaignSettings(type.value, file);
            const appliedWarnings = applySettingsPayload(payload) || [];
            const allWarnings = [...new Set([...warnings, ...appliedWarnings])];
            if (allWarnings.length) toast.info(allWarnings.slice(0, 3).join(' '));
            else toast.success('Настройки загружены в форму');
        } catch (error) {
            notifyHttpError(error, 'Не удалось загрузить настройки');
        } finally {
            importingSettings.value = false;
        }
    };

    const onAdvertiserChange = () => {
        form.brand_id = null;
        if (isAdmin.value || !form.advertiser_id) return;
        const brands = filteredBrands.value;
        if (brands.length === 1) form.brand_id = brands[0].id;
    };

    const onAudienceEnabled = (value) => {
        audienceEnabled.value = Boolean(value);
    };

    const getCreativeReadinessOverride = (creative) => creativeReadinessById.value[Number(creative?.id)] || null;
    const getCreativeReadinessTooltip = creative => buildMissingTargetsTooltip(getCreativeReadinessOverride(creative)?.readiness_details || creative?.readiness_details);
    const isCreativeReadyForImpressions = creative => resolveCreativeReadiness(creative, getCreativeReadinessOverride(creative));
    const isCreativeSelected = id => selectedCreativeIds.value.some(value => Number(value) === Number(id));
    const isCreativePausedInCampaign = (creative) => {
        const attached = (props.campaignCreatives || []).some(item => Number(item.id) === Number(creative?.id));
        return attached && !isCreativeSelected(creative.id);
    };

    const refreshCreativeReadiness = async () => {
        const creativeIds = allCreatives.value.map(creative => Number(creative.id)).filter(id => !Number.isNaN(id));
        if (creativeIds.length === 0 || ((selectedFrameGids?.value || []).length === 0 && !isEditMode.value)) {
            creativeReadinessById.value = {};
            return;
        }
        try {
            const { data } = await axios.post('/api/rtb/campaigns/creative-readiness', {
                creative_ids: creativeIds,
                frame_gids: selectedFrameGids?.value || [],
                campaign_id: isEditMode.value && (selectedFrameGids?.value || []).length === 0 ? props.campaign?.id : null,
            });
            const next = {};
            for (const [id, item] of Object.entries(data.readiness || {})) {
                next[Number(id)] = {
                    ready: Boolean(item?.ready),
                    readiness_details: {
                        coverage: item?.coverage || 'none',
                        missing_targets: item?.missing_targets || [],
                        required_target_count: Number(item?.required_target_count ?? 0),
                        ready_target_count: Number(item?.ready_target_count ?? 0),
                    },
                };
            }
            creativeReadinessById.value = next;
        } catch {
            // подсказка готовности необязательна: форма остаётся рабочей
        }
    };

    const scheduleCreativeReadinessRefresh = () => {
        clearTimeout(readinessRefreshTimer);
        readinessRefreshTimer = setTimeout(refreshCreativeReadiness, 250);
    };

    const fetchAudiencePreview = async () => {
        if (!audienceEnabled.value || !hasBeelineSelected.value || !form.start_date || !form.end_date) {
            audiencePreview.value = null;
            return;
        }
        try {
            const { data } = await axios.post('/api/rtb/audience/preview', {
                gids: selectedFrameGids?.value || [],
                start_date: form.start_date,
                end_date: form.end_date,
                schedule: campaignSchedule.value,
                campaign_rtb_id: props.campaign?.id || null,
            });
            audiencePreview.value = data;
        } catch {
            audiencePreview.value = null;
        }
    };

    const scheduleAudiencePreview = () => {
        clearTimeout(audiencePreviewTimer);
        audiencePreviewTimer = setTimeout(fetchAudiencePreview, 400);
    };

    const recalcDailyLimits = () => {
        const days = campaignDays.value;
        if (days <= 0) return;
        const totalBudget = Number(form.budget_total_limit) || 0;
        const totalImpr = Number(form.impr_total_limit) || 0;
        form.budget_daily_limit = totalBudget > 0 ? Math.round((totalBudget / days) * 100) / 100 : null;
        form.impr_daily_limit = totalImpr > 0 ? Math.ceil(totalImpr / days) : null;
    };

    const confirmLeave = async (href) => {
        if (!dirty.value) {
            window.location.assign(href);
            return;
        }
        const ok = await confirmAction({
            title: 'Уйти без сохранения?',
            message: 'Изменения в форме будут потеряны.',
            confirmLabel: 'Уйти',
            tone: 'danger',
        });
        if (ok) {
            dirty.value = false;
            window.location.assign(href);
        }
    };

    watch(() => [form.name, form.advertiser_id, form.brand_id, form.start_date, form.end_date, form.status, form.system_status, form.screen_package_id], markDirty);
    watch(() => [form.budget_total_limit, form.impr_total_limit, form.budget_daily_limit, form.impr_daily_limit], () => {
        markDirty();
        if (distributeEvenly.value) recalcDailyLimits();
    });
    watch(distributeEvenly, (value) => {
        markDirty();
        if (value) recalcDailyLimits();
    });
    watch(() => form.start_date, (value) => {
        if (form.end_date && value && form.end_date < value) form.end_date = value;
        if (distributeEvenly.value) recalcDailyLimits();
        scheduleAudiencePreview();
    });
    watch(() => form.end_date, () => {
        if (distributeEvenly.value) recalcDailyLimits();
        scheduleAudiencePreview();
    });
    watch(selectedCreativeIds, markDirty, { deep: true });
    watch(campaignSchedule, () => {
        markDirty();
        scheduleAudiencePreview();
    }, { deep: true });
    watch(audienceEnabled, () => {
        markDirty();
        scheduleAudiencePreview();
    });
    watch(allCreatives, scheduleCreativeReadinessRefresh, { deep: true });
    if (selectedFrameGids) {
        watch(selectedFrameGids, () => {
            markDirty();
            scheduleCreativeReadinessRefresh();
            scheduleAudiencePreview();
        });
    }

    onMounted(() => {
        hydrate();
        scheduleCreativeReadinessRefresh();
        scheduleAudiencePreview();
    });

    onBeforeUnmount(() => {
        clearTimeout(audiencePreviewTimer);
        clearTimeout(readinessRefreshTimer);
    });

    return {
        RTB_MATERIAL_FIELDS,
        form,
        errors,
        generalError,
        saving,
        savingAsDraft,
        exportingSettings,
        importingSettings,
        isCurrentlyDraft,
        selectedCreativeIds,
        campaignSchedule,
        audienceEnabled,
        audienceLocked,
        audiencePreview,
        distributeEvenly,
        settingsFileInput,
        dirty,
        today,
        canWrite,
        isReadOnly,
        isEditMode,
        isAdmin,
        isAgency,
        pageTitle,
        cancelUrl,
        isStartDateLocked,
        endDateMin,
        filteredBrands,
        allCreatives,
        campaignDays,
        hasAtLeastOneLimit,
        selectedCreativeObjects,
        uncoveredScreensNotice,
        showCreativeReadinessHint,
        hasBeelineSelected,
        audienceMarkupPercent,
        audienceOfferUrl,
        audiencePayableHint,
        displayStatus,
        displayStatusLabel,
        statusHint,
        willRemoderate,
        canSaveDraft,
        isValid,
        submit,
        submitDraft,
        exportSettings,
        triggerSettingsImport,
        onSettingsFileSelected,
        applySettingsPayload,
        onAdvertiserChange,
        onAudienceEnabled,
        resolveCreative,
        isCreativeSelected,
        isCreativePausedInCampaign,
        isCreativeReadyForImpressions,
        getCreativeReadinessTooltip,
        scheduleCreativeReadinessRefresh,
        scheduleAudiencePreview,
        confirmLeave,
        markDirty,
        buildBasePayload,
        persistCampaign,
        dateInputValue,
    };
};
